import React, { useImperativeHandle } from 'react';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useResizableSection from './useResizableSection';
import DraggableColumn from './DraggableColumn';

const useStyles = buildUseStyles('ResizableBox', (() => ({
  root: {
    position: 'relative',
  },
  column: {
    display: 'flex',
  },
})));

const ResizableBox = React.forwardRef(({
  className,
  children,
  side,
  localStorageName,
  minSize,
  onPickNewSize,
  startCollapsed,
  alwaysShowDivider,
}, ref) => {
  const classes = useStyles();
  const defaultSizeToMin = onPickNewSize !== undefined;
  const {
    sectionRef: columnRef,
    onStartDrag,
    onToggleCollapsed,
    isCollapsed,
  } = useResizableSection(localStorageName, {
    positiveDeltaMeansBigger: side === 'right',
    onPickNewSize,
    defaultSizeToMin,
    minSize,
    startCollapsed,
  });

  useImperativeHandle(ref, () => ({
    toggleCollapsed: onToggleCollapsed,
  }), [onToggleCollapsed]);

  return (
    <Box
      className={classNames(classes.root, {
        [classes.column]: defaultSizeToMin,
      })}
    >
      <Box
        className={className}
        ref={columnRef}
      >
        {children}
      </Box>
      <DraggableColumn
        onDragStart={onStartDrag}
        side={side}
        onToggleCollapsed={onToggleCollapsed}
        isCollapsed={isCollapsed}
        alwaysShowDivider={alwaysShowDivider}
      />
    </Box>
  );
});

ResizableBox.defaultProps = {
  className: '',
};

ResizableBox.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  localStorageName: PropTypes.string.isRequired,
  className: PropTypes.string,
  minSize: PropTypes.string.isRequired,
  onPickNewSize: PropTypes.func,
  startCollapsed: PropTypes.bool,
};

export default ResizableBox;
